/**
 * AdminCouponsTab - Admin coupon management component
 */
import { useState, useEffect, useCallback } from "react";
import { adminAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Search, Plus, Loader2, Tag, Pencil, Trash2, BarChart3,
  Check, X, Calendar, Users, DollarSign, Percent, AlertCircle
} from "lucide-react";

/**
 * Compute the effective state of a coupon based on status, dates, and usage.
 * Returns: "active" | "disabled" | "expired" | "not_started" | "limit_reached"
 */
function getEffectiveState(coupon) {
  // 1. Check manual status first
  if (coupon.status !== "enabled") {
    return "disabled";
  }
  
  const now = new Date();
  
  // 2. Check if before start date
  if (coupon.starts_at) {
    const startsAt = new Date(coupon.starts_at);
    if (now < startsAt) {
      return "not_started";
    }
  }
  
  // 3. Check if past expiry date
  if (coupon.expires_at) {
    const expiresAt = new Date(coupon.expires_at);
    if (now > expiresAt) {
      return "expired";
    }
  }
  
  // 4. Check usage limit
  if (coupon.usage_limit !== null && coupon.usage_limit !== undefined) {
    if (coupon.times_used >= coupon.usage_limit) {
      return "limit_reached";
    }
  }
  
  // 5. All checks passed - coupon is active
  return "active";
}

/**
 * Get badge styling for effective state
 */
function getEffectiveStateBadge(state) {
  switch (state) {
    case "active":
      return { label: "Active", className: "bg-green-100 text-green-700" };
    case "disabled":
      return { label: "Disabled", className: "bg-slate-100 text-slate-600" };
    case "expired":
      return { label: "Expired", className: "bg-red-100 text-red-700" };
    case "not_started":
      return { label: "Not Started", className: "bg-amber-100 text-amber-700" };
    case "limit_reached":
      return { label: "Limit Reached", className: "bg-orange-100 text-orange-700" };
    default:
      return { label: "Unknown", className: "bg-slate-100 text-slate-600" };
  }
}

export function AdminCouponsTab({ onPinRequired }) {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    code: "",
    status: "enabled",
    discount_type: "flat",
    discount_value: "",
    starts_at: "",
    expires_at: "",
    usage_limit: "",
    notes: "",
  });
  const [formLoading, setFormLoading] = useState(false);
  
  // Stats state
  const [showStats, setShowStats] = useState(null);
  const [statsData, setStatsData] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const fetchCoupons = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminAPI.listCoupons({ page, limit: 20, search });
      setCoupons(res.data.coupons);
      setTotalPages(res.data.total_pages);
    } catch (err) {
      toast.error("Failed to load coupons");
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    fetchCoupons();
  }, [fetchCoupons]);

  const resetForm = () => {
    setFormData({
      name: "",
      code: "",
      status: "enabled",
      discount_type: "flat",
      discount_value: "",
      starts_at: "",
      expires_at: "",
      usage_limit: "",
      notes: "",
    });
    setEditingCoupon(null);
    setShowForm(false);
  };

  const handleEdit = (coupon) => {
    setFormData({
      name: coupon.name,
      code: coupon.code,
      status: coupon.status,
      discount_type: coupon.discount_type,
      discount_value: coupon.discount_value.toString(),
      starts_at: coupon.starts_at ? coupon.starts_at.split("T")[0] : "",
      expires_at: coupon.expires_at ? coupon.expires_at.split("T")[0] : "",
      usage_limit: coupon.usage_limit?.toString() || "",
      notes: coupon.notes || "",
    });
    setEditingCoupon(coupon);
    setShowForm(true);
  };

  const handleSubmit = async (pin) => {
    if (!formData.name.trim() || !formData.code.trim() || !formData.discount_value) {
      toast.error("Please fill in required fields");
      return;
    }

    setFormLoading(true);
    try {
      const payload = {
        name: formData.name.trim(),
        code: formData.code.trim().toUpperCase(),
        status: formData.status,
        discount_type: formData.discount_type,
        discount_value: parseFloat(formData.discount_value),
        starts_at: formData.starts_at ? new Date(formData.starts_at).toISOString() : null,
        expires_at: formData.expires_at ? new Date(formData.expires_at).toISOString() : null,
        usage_limit: formData.usage_limit ? parseInt(formData.usage_limit) : null,
        notes: formData.notes || null,
      };

      if (editingCoupon) {
        await adminAPI.updateCoupon(editingCoupon.id, payload, pin);
        toast.success("Coupon updated");
      } else {
        await adminAPI.createCoupon(payload, pin);
        toast.success("Coupon created");
      }
      resetForm();
      fetchCoupons();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save coupon");
      throw err;
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (coupon, pin) => {
    try {
      await adminAPI.deleteCoupon(coupon.id, pin);
      toast.success("Coupon deleted");
      fetchCoupons();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete coupon");
      throw err;
    }
  };

  const handleViewStats = async (coupon) => {
    setShowStats(coupon);
    setStatsLoading(true);
    try {
      const res = await adminAPI.getCouponStats(coupon.id);
      setStatsData(res.data);
    } catch (err) {
      toast.error("Failed to load statistics");
      setShowStats(null);
    } finally {
      setStatsLoading(false);
    }
  };

  const handleToggleStatus = async (coupon, pin) => {
    const newStatus = coupon.status === "enabled" ? "disabled" : "enabled";
    try {
      await adminAPI.updateCoupon(coupon.id, { status: newStatus }, pin);
      toast.success(`Coupon ${newStatus}`);
      fetchCoupons();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update status");
      throw err;
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-coupons-tab">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="relative flex-1 max-w-md w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search coupons..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="pl-10 h-9 text-sm"
            data-testid="coupon-search"
          />
        </div>
        <Button
          size="sm"
          onClick={() => setShowForm(true)}
          className="bg-[#18181B] hover:bg-[#27272A]"
          data-testid="create-coupon-btn"
        >
          <Plus className="h-4 w-4 mr-1" />
          Create Coupon
        </Button>
      </div>

      {/* Coupon Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={resetForm} />
          <div className="relative z-10 bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-bold text-[#09090B] mb-4" style={{ fontFamily: "Manrope, sans-serif" }}>
              {editingCoupon ? "Edit Coupon" : "Create New Coupon"}
            </h3>
            
            <div className="space-y-4">
              {/* General Settings */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Coupon Name *</label>
                  <Input
                    placeholder="e.g., Launch Offer"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="h-9 text-sm"
                    data-testid="coupon-name-input"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Coupon Code *</label>
                  <Input
                    placeholder="e.g., LAUNCH50"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    className="h-9 text-sm uppercase"
                    data-testid="coupon-code-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Discount Type</label>
                  <Select
                    value={formData.discount_type}
                    onValueChange={(v) => setFormData({ ...formData, discount_type: v })}
                  >
                    <SelectTrigger className="h-9 text-sm" data-testid="discount-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="flat">Flat (₹)</SelectItem>
                      <SelectItem value="percentage">Percentage (%)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Discount Value *</label>
                  <Input
                    type="number"
                    placeholder={formData.discount_type === "percentage" ? "e.g., 20" : "e.g., 200"}
                    value={formData.discount_value}
                    onChange={(e) => setFormData({ ...formData, discount_value: e.target.value })}
                    className="h-9 text-sm"
                    data-testid="discount-value-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Status</label>
                <Select
                  value={formData.status}
                  onValueChange={(v) => setFormData({ ...formData, status: v })}
                >
                  <SelectTrigger className="h-9 text-sm" data-testid="status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="enabled">Enabled</SelectItem>
                    <SelectItem value="disabled">Disabled</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Coupon Settings */}
              <div className="border-t pt-4">
                <h4 className="text-sm font-medium text-slate-700 mb-3">Validity & Limits</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Start Date (optional)</label>
                    <Input
                      type="date"
                      value={formData.starts_at}
                      onChange={(e) => setFormData({ ...formData, starts_at: e.target.value })}
                      className="h-9 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Expiry Date (optional)</label>
                    <Input
                      type="date"
                      value={formData.expires_at}
                      onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                      className="h-9 text-sm"
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Usage Limit (optional)
                    <span className="ml-1 text-slate-400 font-normal">— max successful completed uses</span>
                  </label>
                  <Input
                    type="number"
                    placeholder="Leave empty for unlimited"
                    value={formData.usage_limit}
                    onChange={(e) => setFormData({ ...formData, usage_limit: e.target.value })}
                    className="h-9 text-sm"
                  />
                </div>
                <div className="mt-4">
                  <label className="block text-xs font-medium text-slate-600 mb-1">Notes (optional)</label>
                  <Input
                    placeholder="Internal notes about this coupon"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="h-9 text-sm"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" size="sm" onClick={resetForm}>Cancel</Button>
              <Button
                size="sm"
                disabled={formLoading}
                onClick={() => onPinRequired(editingCoupon ? "Update coupon" : "Create coupon", handleSubmit)}
                className="bg-[#18181B] hover:bg-[#27272A]"
                data-testid="save-coupon-btn"
              >
                {formLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                {editingCoupon ? "Update" : "Create"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Stats Modal */}
      {showStats && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center" data-testid="coupon-stats-modal">
          <div className="absolute inset-0 bg-black/50" onClick={() => { setShowStats(null); setStatsData(null); }} />
          <div className="relative z-10 bg-white rounded-xl shadow-xl max-w-4xl w-full mx-4 p-6 max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between mb-4 shrink-0">
              <div>
                <h3 className="text-lg font-bold text-[#09090B]" style={{ fontFamily: "Manrope, sans-serif" }}>
                  Coupon Statistics
                </h3>
                <div className="flex items-center gap-2 mt-1">
                  <span className="font-mono bg-slate-100 px-2 py-0.5 rounded text-xs">{showStats.code}</span>
                  {showStats.name && <span className="text-sm text-slate-400">• {showStats.name}</span>}
                  {(() => {
                    const effectiveState = getEffectiveState(showStats);
                    const badge = getEffectiveStateBadge(effectiveState);
                    return (
                      <Badge className={`${badge.className} text-[10px] ml-1`}>
                        {badge.label}
                      </Badge>
                    );
                  })()}
                </div>
              </div>
              <Button variant="ghost" size="icon" onClick={() => { setShowStats(null); setStatsData(null); }} data-testid="close-stats-modal">
                <X className="h-4 w-4" />
              </Button>
            </div>
            
            {statsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
              </div>
            ) : statsData ? (
              <>
                {/* Summary Metrics Section */}
                <div className="shrink-0 mb-6">
                  <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Summary Metrics</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-slate-50 rounded-lg p-4 text-center border border-slate-100" data-testid="stat-times-used">
                      <Users className="h-5 w-5 text-slate-400 mx-auto mb-1" />
                      <p className="text-2xl font-bold text-[#09090B]">{statsData.total_times_used}</p>
                      <p className="text-xs text-slate-500">Total Times Used</p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4 text-center border border-green-100" data-testid="stat-revenue">
                      <DollarSign className="h-5 w-5 text-green-500 mx-auto mb-1" />
                      <p className="text-2xl font-bold text-green-700">₹{statsData.total_revenue.toLocaleString("en-IN")}</p>
                      <p className="text-xs text-slate-500">Total Revenue Generated</p>
                    </div>
                    <div className="bg-orange-50 rounded-lg p-4 text-center border border-orange-100" data-testid="stat-discount">
                      <Percent className="h-5 w-5 text-orange-500 mx-auto mb-1" />
                      <p className="text-2xl font-bold text-orange-700">₹{statsData.total_discount_given.toLocaleString("en-IN")}</p>
                      <p className="text-xs text-slate-500">Total Discount Given</p>
                    </div>
                  </div>
                </div>

                {/* Detailed Usage Table Section */}
                <div className="flex-1 min-h-0 flex flex-col">
                  <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3 shrink-0">Usage Details</h4>
                  {statsData.usage_records.length > 0 ? (
                    <div className="border rounded-lg overflow-hidden flex-1 min-h-0">
                      <div className="overflow-auto max-h-[300px]">
                        <table className="w-full text-sm" data-testid="coupon-usage-table">
                          <thead className="bg-slate-50 sticky top-0">
                            <tr>
                              <th className="text-left px-3 py-2.5 text-xs font-medium text-slate-500">Name</th>
                              <th className="text-left px-3 py-2.5 text-xs font-medium text-slate-500">Phone</th>
                              <th className="text-left px-3 py-2.5 text-xs font-medium text-slate-500">Email</th>
                              <th className="text-left px-3 py-2.5 text-xs font-medium text-slate-500">Product Name</th>
                              <th className="text-right px-3 py-2.5 text-xs font-medium text-slate-500">Discount Amount</th>
                              <th className="text-right px-3 py-2.5 text-xs font-medium text-slate-500">Final Amount Paid</th>
                              <th className="text-left px-3 py-2.5 text-xs font-medium text-slate-500">Date Used</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {statsData.usage_records.map((record, i) => (
                              <tr key={i} className="hover:bg-slate-50/50" data-testid={`usage-row-${i}`}>
                                <td className="px-3 py-2.5 text-xs font-medium text-[#09090B]">
                                  {record.name || <span className="text-slate-400 italic">—</span>}
                                </td>
                                <td className="px-3 py-2.5 text-xs text-slate-600">{record.phone}</td>
                                <td className="px-3 py-2.5 text-xs text-slate-500 max-w-[150px] truncate" title={record.email || ''}>
                                  {record.email || <span className="text-slate-400">—</span>}
                                </td>
                                <td className="px-3 py-2.5 text-xs text-slate-600">{record.product_name}</td>
                                <td className="px-3 py-2.5 text-xs text-right text-orange-600 font-medium">
                                  -₹{(record.discount_amount / 100).toLocaleString("en-IN")}
                                </td>
                                <td className="px-3 py-2.5 text-xs text-right font-semibold text-green-700">
                                  ₹{(record.final_amount / 100).toLocaleString("en-IN")}
                                </td>
                                <td className="px-3 py-2.5 text-xs text-slate-500">
                                  {new Date(record.used_at).toLocaleDateString("en-IN", {
                                    day: "numeric",
                                    month: "short",
                                    year: "numeric"
                                  })}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <div className="flex-1 flex items-center justify-center border rounded-lg bg-slate-50/50">
                      <div className="text-center py-12">
                        <Users className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                        <p className="text-sm text-slate-500">No usage records yet</p>
                        <p className="text-xs text-slate-400 mt-1">Usage will appear here once customers use this coupon</p>
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : null}
          </div>
        </div>
      )}

      {/* Coupons Table */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        </div>
      ) : coupons.length === 0 ? (
        <div className="text-center py-20">
          <Tag className="h-12 w-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">No coupons found</p>
          <Button
            variant="link"
            onClick={() => setShowForm(true)}
            className="mt-2 text-sm"
          >
            Create your first coupon
          </Button>
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-500">Coupon</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-500">Discount</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-500">Validity</th>
                <th className="text-center px-4 py-3 text-xs font-medium text-slate-500">Usage</th>
                <th className="text-center px-4 py-3 text-xs font-medium text-slate-500">Status</th>
                <th className="text-center px-4 py-3 text-xs font-medium text-slate-500">Effective State</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-slate-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {coupons.map((coupon) => (
                <tr key={coupon.id} className="hover:bg-slate-50" data-testid={`coupon-row-${coupon.code}`}>
                  <td className="px-4 py-3">
                    <p className="font-medium text-[#09090B]">{coupon.name}</p>
                    <p className="text-xs text-slate-500 font-mono">{coupon.code}</p>
                  </td>
                  <td className="px-4 py-3">
                    {coupon.discount_type === "percentage" ? (
                      <span className="text-green-600 font-medium">{coupon.discount_value}% off</span>
                    ) : (
                      <span className="text-green-600 font-medium">₹{coupon.discount_value} off</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">
                    {coupon.starts_at || coupon.expires_at ? (
                      <>
                        {coupon.starts_at && <span>{new Date(coupon.starts_at).toLocaleDateString("en-IN")}</span>}
                        {coupon.starts_at && coupon.expires_at && " - "}
                        {coupon.expires_at && <span>{new Date(coupon.expires_at).toLocaleDateString("en-IN")}</span>}
                      </>
                    ) : (
                      <span className="text-green-600">No expiry</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="font-medium">{coupon.times_used}</span>
                    {coupon.usage_limit && <span className="text-slate-400">/{coupon.usage_limit}</span>}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <Badge
                      variant="outline"
                      className={coupon.status === "enabled" ? "border-green-300 text-green-700" : "border-slate-300 text-slate-500"}
                    >
                      {coupon.status === "enabled" ? "Enabled" : "Disabled"}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {(() => {
                      const effectiveState = getEffectiveState(coupon);
                      const badge = getEffectiveStateBadge(effectiveState);
                      return (
                        <Badge className={`${badge.className} hover:${badge.className}`} data-testid={`effective-state-${coupon.code}`}>
                          {badge.label}
                        </Badge>
                      );
                    })()}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleViewStats(coupon)}
                        title="View statistics"
                        data-testid={`stats-btn-${coupon.code}`}
                      >
                        <BarChart3 className="h-4 w-4 text-slate-400" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => onPinRequired(
                          coupon.status === "enabled" ? "Disable coupon" : "Enable coupon",
                          (pin) => handleToggleStatus(coupon, pin)
                        )}
                        title={coupon.status === "enabled" ? "Disable" : "Enable"}
                      >
                        {coupon.status === "enabled" ? (
                          <X className="h-4 w-4 text-orange-500" />
                        ) : (
                          <Check className="h-4 w-4 text-green-500" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleEdit(coupon)}
                        title="Edit"
                        data-testid={`edit-btn-${coupon.code}`}
                      >
                        <Pencil className="h-4 w-4 text-slate-400" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => onPinRequired("Delete coupon", (pin) => handleDelete(coupon, pin))}
                        title="Delete"
                        data-testid={`delete-btn-${coupon.code}`}
                      >
                        <Trash2 className="h-4 w-4 text-red-400" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            Previous
          </Button>
          <span className="text-sm text-slate-500">Page {page} of {totalPages}</span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
